import { useQuery } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import {
  Button,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  FluentProvider,
  Spinner,
  webLightTheme,
  TableColumnDefinition,
  createTableColumn,
  TableCellLayout,
} from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CoherencePanelStyles } from '../../../../app/common/styles/CommonStyleObjects';
import { GET_PROCESS_TYPE_WITH_MILESTONE_POOL } from '../../../../utils/statApi/ProcessTypesApi';

import { IMilestoneTemplate } from '../../../../utils/types/IMilestoneTemplate';
import { IProcessTemplateMilestone } from '../../../../utils/types/IProcessTemplate';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';
import { IProcessTemplateTypeParams } from '../../../common/ParamTypes';

interface IProcessTemplateMilestoneAddProps {
  closePanel: () => void;
  onAddSave: (selectedMilestone: IMilestoneTemplate, index: number) => void;
  index: number;
  currentMilestones: IProcessTemplateMilestone[];
}
const ProcessTemplateMilestoneAdd = (props: IProcessTemplateMilestoneAddProps): JSX.Element => {
  const { closePanel, onAddSave, index, currentMilestones } = props;

  const [selectedMilestone, setSelectedMilestone] = useState<IMilestoneTemplate | null>(null);

  const onSaveClick = () => {
    // TODO: make sure this is not null
    onAddSave(selectedMilestone, index);
  };

  const { processTypeId } = useParams<IProcessTemplateTypeParams>();
  const processTypeIdInt = parseInt(processTypeId, 10);

  const { data, loading, error } = useQuery(GET_PROCESS_TYPE_WITH_MILESTONE_POOL, {
    variables: { processTypeId: processTypeIdInt },
  });
  const [newMilestoneTemplates, setNewMilestoneTemplates] = useState<IProcessTemplateMilestone[]>(
    [],
  );
  useEffect(() => {
    if (data?.processTypeWithMilestonePool.milestoneTemplates) {
      const currentMilestoneIds = currentMilestones.map((mi) => mi.milestoneTemplateId);
      const unusedMilestoneTemplates = data?.processTypeWithMilestonePool.milestoneTemplates.filter(
        (mi: IProcessTemplateMilestone) => {
          return !currentMilestoneIds.includes(mi.id);
        },
      );
      setNewMilestoneTemplates(unusedMilestoneTemplates);
    }
  }, [data, currentMilestones]);

  const itemColumns: TableColumnDefinition<IProcessTemplateMilestone>[] = [
    createTableColumn<IProcessTemplateMilestone>({
      columnId: 'milestoneName',
      renderHeaderCell: () => {
        return 'Milestone Name';
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.name}</TableCellLayout>;
      },
    }),
    createTableColumn<IProcessTemplateMilestone>({
      columnId: 'milestoneCategory',
      renderHeaderCell: () => {
        return 'Category';
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.milestoneCategoryName}</TableCellLayout>;
      },
    }),
  ];

  const handleSelectionChange = (
    e: React.MouseEvent | React.KeyboardEvent,
    dataItem: { selectedItems: Set<string | number> },
  ) => {
    const selectedId = Array.from(dataItem.selectedItems)[0];

    if (selectedId !== undefined) {
      const selected = newMilestoneTemplates.find((item) => item.id === selectedId);
      setSelectedMilestone(selected as unknown as IMilestoneTemplate);
    } else {
      setSelectedMilestone(null);
    }
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Add Milestone"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      closeButtonAriaLabel="Close"
      onRenderFooter={() => (
        <FluentProvider theme={webLightTheme}>
          <Button appearance="primary" onClick={onSaveClick} disabled={selectedMilestone === null}>
            Add Selected Milestone
          </Button>
        </FluentProvider>
      )}
    >
      <LoadingErrorMessage loading={loading} error={error} />
      {loading && !error && (
        <FluentProvider theme={webLightTheme}>
          <Spinner label="Loading Milestones" />
        </FluentProvider>
      )}
      {!loading && !error && (
        <>
          Select a milestone from the milestone pool
          <FluentProvider theme={webLightTheme}>
            <DataGrid
              items={newMilestoneTemplates}
              columns={itemColumns}
              selectionMode="single"
              focusMode="composite"
              getRowId={(item) => item.id}
              onSelectionChange={handleSelectionChange}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => (
                    <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<IProcessTemplateMilestone>>
                {({ item, rowId }) => (
                  <DataGridRow<IProcessTemplateMilestone>
                    key={rowId}
                    selectionCell={{
                      checkboxIndicator: { 'aria-label': 'Select row' },
                    }}
                  >
                    {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          </FluentProvider>
        </>
      )}
    </CoherencePanel>
  );
};
export default ProcessTemplateMilestoneAdd;
